import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';

import { useChat } from '@/components/providers/ChatProvider';
import { useFeatureFlags } from '@/components/providers/FeatureFlagsProvider';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { awsCredentialsQueries } from '@/lib/queries/awsCredentials';
import { awsRoleQueries } from '@/lib/queries/awsRole';
import { githubConfigQueries } from '@/lib/queries/githubConfig';
import { monitoringQueries } from '@/lib/queries/monitoring';
import { preLoadDefaultProject } from '@/lib/queries/project';
import { terraformBackendQueries } from '@/lib/queries/terraformBackend';

import { Integrations } from './-components/Integrations';
import { TryIt } from './-components/TryIt';
import { getIntegrationData } from './-data';

export const Route = createFileRoute('/_root/integrations/')({
  component: RouteComponent,
  validateSearch: (s: Record<string, unknown>) =>
    z
      .object({
        github: z.string().optional(),
      })
      .parse(s),
  loader: async ({ context }) => {
    const defaultProject = await preLoadDefaultProject({ context });
    context.queryClient.prefetchQuery(
      githubConfigQueries.list(defaultProject.id),
    );
    context.queryClient.prefetchQuery(
      awsCredentialsQueries.list(defaultProject.id),
    );
    context.queryClient.prefetchQuery(awsRoleQueries.list(defaultProject.id));

    return defaultProject;
  },
});

function RouteComponent() {
  const defaultProject = Route.useLoaderData();
  const { github } = Route.useSearch();
  const { FeatureDatadogIntegration } = useFeatureFlags();
  const { setPrompt } = useChat();
  const { toast, dismiss } = useToast();

  const { data: githubInstall } = useQuery(
    githubConfigQueries.list(defaultProject.id),
  );

  const { data: awsUsers } = useQuery(
    awsCredentialsQueries.list(defaultProject.id),
  );

  const { data: awsRoles } = useQuery(awsRoleQueries.list(defaultProject.id));

  const { data: buckets } = useQuery(
    terraformBackendQueries.listBuckets(defaultProject.id),
  );

  const { data: hcpCloudTokens } = useQuery(
    terraformBackendQueries.listHcpCloudTokens(defaultProject.id),
  );

  const { data: datadogApiCredentials } = useQuery(
    monitoringQueries.listDatadogApiCredentials(defaultProject.id),
  );

  const integrationsData = useMemo(
    () =>
      getIntegrationData({
        awsRoles,
        awsUsers,
        githubInstall,
        buckets,
        hcpCloudTokens,
        datadogApiCredentials,
        datadogEnabled: FeatureDatadogIntegration === true,
      }),
    [
      awsRoles,
      awsUsers,
      githubInstall,
      buckets,
      hcpCloudTokens,
      datadogApiCredentials,
      FeatureDatadogIntegration,
    ],
  );

  const [fired, setFired] = useState(false);
  useEffect(() => {
    if (!fired && github === 'installed') {
      setFired(true);
      toast({
        title: 'githubCallback.installationSuccess',
        variant: 'default',
        description: 'integrations.scm.tryIt',
        descriptionValues: {
          button: parts => (
            <>
              <br />
              <Button
                variant="main"
                className="mt-2"
                onClick={() => {
                  dismiss();
                  setPrompt(
                    "I'm modifying a Terraform module. List dependent EC2 instances.",
                  );
                }}
              >
                {parts}
              </Button>
            </>
          ),
        },
      });
    }
  }, [setPrompt, fired, toast, dismiss, github]);

  return (
    <>
      <h1 className="text-3xl">
        <FormattedMessage id="integrations.title" />
      </h1>
      <Integrations
        data={integrationsData}
        defaultProjectId={defaultProject.id}
      />
      <TryIt
        awsUsers={awsUsers}
        awsRoles={awsRoles}
        githubInstall={githubInstall}
        buckets={buckets}
      />
    </>
  );
}
